/**
 * La espera mientras se confirma el pago.
 *
 * <h2>Que problema resuelve</h2>
 *
 * <p>Hasta el 2026-09-09 este momento eran <strong>dos lineas de texto centradas y nada mas</strong>:
 * ni spinner, ni icono, ni forma. Y es el momento en que el comprador MAS mira la pantalla, porque
 * ya pago y todavia no sabe si salio bien. Una espera de varios segundos sin un solo elemento en
 * movimiento no se lee como «estamos confirmando»: se lee como una pagina colgada, y lo que hace
 * entonces el comprador es recargar o cerrar —que es exactamente lo que el texto le pide que no
 * haga—.
 *
 * <h2>Por que se parece tanto a HandoffScreen</h2>
 *
 * <p>Comparte su altura reservada (11rem) a proposito. Las dos son esperas y en algunos flujos van
 * seguidas —traspaso a la pasarela, vuelta, confirmacion—; con alturas distintas el panel daria un
 * salto justo en el tramo donde el comprador esta mas pendiente. No se fusionan en un solo
 * componente porque dicen cosas distintas: {@link HandoffScreen} acompana un traslado y esta
 * acompana una espera con el dinero ya movido, con un titulo y una instruccion propios.
 */
import type { JSX } from 'react';
import type { Locale } from '../i18n';
import { widgetText } from './copy';
import { MUTED_TEXT_CLASS, SCREEN_TITLE_CLASS, SURFACE_CLASS } from './styles';

export interface ProcessingScreenProps {
  locale: Locale;
}

/*
 * Misma altura reservada que el traspaso (ver arriba), y EN TARJETA.
 *
 * Lo de la tarjeta no es cosmetico: encima de esta pantalla va el resumen del pedido, que si es una
 * tarjeta. Sin superficie propia, la espera quedaba como texto suelto colgando bajo una tarjeta, y
 * eso es lo que hacia que el momento mas delicado del checkout pareciera una pagina a medio cargar
 * en vez de un paso del proceso.
 */
const WRAPPER_CLASS =
  SURFACE_CLASS +
  ' flex min-h-[11rem] flex-col items-center justify-center gap-3 py-8 text-center';

/**
 * El spinner va DENTRO de un disco teñido con el color del comercio.
 *
 * <p>El disco no es adorno: da al indicador el mismo peso visual que el icono de las pantallas
 * terminales, de modo que la secuencia «esperando -> desenlace» se ve como una misma pieza que
 * cambia de estado, y no como dos pantallas de familias distintas.
 */
const HALO_CLASS =
  'grid h-14 w-14 place-items-center rounded-full bg-[var(--apipay-primary-050,#f5f8ff)] ' +
  'ring-1 ring-[var(--apipay-primary-200,#c7d7fe)]';

const SPINNER_CLASS =
  'h-7 w-7 animate-spin motion-reduce:animate-none text-[var(--apipay-color-primary,#3a4fd9)]';

export function ProcessingScreen({ locale }: ProcessingScreenProps): JSX.Element {
  return (
    <div
      className={WRAPPER_CLASS}
      data-testid="processing-indicator"
      role="status"
      aria-live="polite"
    >
      <span className={HALO_CLASS}>
        <svg
          className={SPINNER_CLASS}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          {/* La pista completa, tenue: el circulo de referencia sobre el que se lee el arco. */}
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.18" />
          <path
            d="M22 12a10 10 0 0 0-10-10"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </span>

      <h2 className={SCREEN_TITLE_CLASS}>{widgetText(locale, 'processing.title')}</h2>
      {/*
       * La instruccion va en su propia linea y no dentro del titulo: es lo UNICO que el comprador
       * tiene que hacer en esta pantalla, y cerrar la ventana aqui es lo que deja un cobro sin
       * cerrar en las pasarelas que no tienen webhook.
       */}
      <p className={MUTED_TEXT_CLASS}>{widgetText(locale, 'processing.body')}</p>
    </div>
  );
}
